import React, { type ReactNode } from 'react';
import clsx from 'clsx';
import { RadioGroupFormField, type RadioGroupFormFieldProps } from '~/shared/forms/controls/fields/RadioGroupFormField';
import type { FormControlOptionModelExtended } from '../types';
import type { useFormFieldResult } from '~/shared/forms/useFormField';
import { Radio, type RadioProps } from '~/shared/forms/controls/base-controls/Radio';

const getOptions = (
	fieldProps: useFormFieldResult<string | null>,
	options: FormControlOptionModelExtended[],
	size?: RadioProps['size']
): ReactNode => {
	const renderedOptions = options.map((option, index) => {
		const optionIsSelected = fieldProps.value === option.value;
		const firstInGroup =
			option.groupLabel &&
			(index === 0 ||
				!(options![index - 1] as FormControlOptionModelExtended).groupLabel ||
				(options![index - 1] as FormControlOptionModelExtended).groupLabel !== option.groupLabel);

		const className = clsx(option.className, optionIsSelected ? option.selectedClassName : null, option.groupLabel ? 'pt-4' : null);

		const grouping =
			firstInGroup && option.groupLabel ? <h5 className="mb-1 mt-3 text-sm font-semibold text-gray-800">{option.groupLabel}</h5> : null;

		if (option.children) {
			return (
				<React.Fragment key={option.value}>
					{grouping}
					<div className={className}>
						<Radio size={size} {...option} />
						{optionIsSelected ? <div className="mt-4 space-y-4 pl-8">{option.children}</div> : null}
					</div>
				</React.Fragment>
			);
		}

		return (
			<React.Fragment key={option.value}>
				{grouping}
				<Radio {...option} size={size} className={className} />
			</React.Fragment>
		);
	});

	if (size !== 'small' || renderedOptions.length <= 7) {
		return renderedOptions;
	}

	const mid = Math.ceil(renderedOptions.length / 2);
	const left = renderedOptions.slice(0, mid);
	const right = renderedOptions.slice(mid);

	return (
		<div className="grid grid-cols-2 gap-2">
			<div>{left}</div>
			<div>{right}</div>
		</div>
	);
};

export function RadioGroupFormFieldWithChildren(props: Omit<RadioGroupFormFieldProps, 'options'> & { options: FormControlOptionModelExtended[] }) {
	return <RadioGroupFormField {...props} options={undefined} getOptions={fieldProps => getOptions(fieldProps, props.options, props.size)} />;
}
